import type { IntlShape } from 'react-intl';

import type {
	Command,
	ExtractInjectionAPI,
	FloatingToolbarItem,
} from '@atlaskit/editor-common/types';
import type { Node as PMNode, Schema } from '@atlaskit/editor-prosemirror/model';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import { fg } from '@atlaskit/platform-feature-flags';
import { getRemixSwitcherInfo } from '@atlassian/malleable-ui-components/remix/remix-overlay/utils';
import { buildRemixTypeSwitcherDropdown } from '@atlassian/malleable-ui-components/remix/remix-type-switcher';
import type { RemixHistoryEntryForUpdate } from '@atlassian/malleable-ui-components/types';

import { onStartRemixThinking } from '../../../editor-commands/start-remix-thinking';
import type {
	GetRemixVariantFloatingToolbarButton,
	MalleableUiPlugin,
} from '../../../malleableUiPluginType';
import { getHistoryEntryForUpdate } from '../../../pm-plugins/remix-history/getHistoryEntryForUpdate';

/**
 * Resolve the `mediaFileId` (i.e. `media.attrs.id`) for the node that the
 * Ask Rovo floating toolbar item is being rendered for. Mirrors the keying
 * used by `aiGeneratingDecorationPluginKey` (which matches on `media.attrs.id`)
 * and by the bridge's `previousMediaFileId` ACTION_PREPARE payload, so an
 * optimistic submit-time set on this id is idempotent with the eventual
 * server-confirmed set.
 */
export function getMediaFileIdFromAskRovoNode(node: PMNode): string | undefined {
	if (node.type.name === 'media') {
		const id = node.attrs?.id;
		return typeof id === 'string' && id.length > 0 ? id : undefined;
	}
	if (node.type.name === 'mediaSingle') {
		const inner = node.firstChild;
		const id = inner?.attrs?.id;
		return typeof id === 'string' && id.length > 0 ? id : undefined;
	}
	return undefined;
}

export function getRemixNodeHistoryEntry({
	node,
	schema,
	state,
}: {
	node: PMNode;
	schema: Schema;
	state: EditorState;
}): RemixHistoryEntryForUpdate | undefined {
	const { media, mediaSingle } = schema.nodes;
	const isMaui = node.attrs.extensionKey === 'native-embed:maui';
	const isWhiteboard = node.attrs.extensionKey === 'native-embed:maui-whiteboard';
	if (isMaui || isWhiteboard) {
		const url = node.attrs.parameters?.macroParams?.url?.value;
		return getHistoryEntryForUpdate({ state, url });
	}

	const isMedia = node.type === media || node.type === mediaSingle;
	if (isMedia) {
		const mediaNode = node.type === media ? node : node.firstChild;
		if (mediaNode) {
			const fieldId = mediaNode.attrs.id;
			if (fieldId) {
				return getHistoryEntryForUpdate({ state, fieldId });
			}
		}
	}
}

/**
 * Builds the type-switcher dropdown toolbar items for a node with remix history.
 * Shared by both the native-embeds toolbar (via makeRemixVariantFloatingToolbarButton)
 * and the media floating toolbar (via overridden-floating-toolbar-config).
 */
export function buildTypeSwitcherDropdownItems({
	node,
	schema,
	state,
	intl,
	disabled,
	api,
}: {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
	disabled: boolean;
	intl: IntlShape;
	node: PMNode;
	schema: Schema;
	state: EditorState;
}): FloatingToolbarItem<Command> | undefined {
	if (!fg('platform_editor_ai_remix_toolbar')) {
		return undefined;
	}
	const historyEntry = getRemixNodeHistoryEntry({ node, schema, state });
	if (!historyEntry) {
		return undefined;
	}
	const switcherInfo = getRemixSwitcherInfo(
		historyEntry.dynamicUiType,
		historyEntry.dynamicUiSubtype,
	);
	if (!switcherInfo) {
		return undefined;
	}
	const dropdown = buildRemixTypeSwitcherDropdown({
		dynamicUiType: historyEntry.dynamicUiType,
		dynamicUiSubtype: historyEntry.dynamicUiSubtype,
		currentLabel: switcherInfo.currentLabel,
		groupLabelMessage: switcherInfo.groupLabelMessage,
		siblingSubTypes: switcherInfo.siblingSubTypes,
		intl,
		disabled,
		onSelect: ({ subtype }) => {
			const targetId = getMediaFileIdFromAskRovoNode(node) || node.attrs.localId;
			if (targetId) {
				api?.aiExperience?.actions?.stageInlineRovoStreaming?.(targetId);
				api?.aiExperience?.actions?.commitInlineRovoStreaming?.();
			}
			onStartRemixThinking({
				api,
				options: {
					isUpdate: true,
					historyEntryForUpdate: historyEntry,
					dynamicUiSubtypeForUpdate: subtype,
				},
			});
		},
	});
	return dropdown ?? undefined;
}

/**
 * Factory that returns the `getRemixVariantFloatingToolbarButton` action.
 *
 * When the selected node has remix history, builds a type-switcher dropdown
 * so the user can switch the remix subtype directly from the floating toolbar.
 * The dropdown is consumed by the native-embeds toolbar registry
 * (`BUILTIN_TOOLBAR_KEYS.REMIX_VARIANT`).
 */
export function makeRemixVariantFloatingToolbarButton({
	api,
}: {
	api?: ExtractInjectionAPI<MalleableUiPlugin>;
}): GetRemixVariantFloatingToolbarButton {
	return ({ schema, state, node, intl }) => {
		if (!node) {
			return;
		}

		const disabled = Boolean(
			api?.malleableUi?.sharedState.currentState()?.isRemixModalOpen ||
				api?.malleableUi?.sharedState.currentState()?.isThinking ||
				api?.aiExperience?.sharedState.currentState()?.areAiEntryPointsDisabled,
		);

		return buildTypeSwitcherDropdownItems({
			node,
			schema,
			state,
			intl,
			disabled,
			api,
		});
	};
}
